import React, { FormEvent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Element } from 'react-scroll';
import { always, complement, isEmpty, isNil } from 'ramda';
import { useAsync } from 'react-use';

import { GalleryPage, Address, OnPlatform, OnPlatformArtist } from '@definitions/galleries.types';
import { ProductsBlock, ImageBlock, NoArtworks, NewsletterBlock } from '@components/blocks';
import { JumpToSection as JumpToSectionButton } from '@components/jumptoSectionButtons';
import { Block, ImageSet } from '@definitions/common.types';
import { renderWhen, renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { useMonogram } from '@hooks/useMonogram';
import { useCustomer } from '@hooks/useCustomer';
import { capitalizeWords, getHasProducts } from '@utils/helpers';
import { NotifyForm } from '@components/notifyForm';
import { useTrackConversions } from '@hooks/useTrackConversions';
import { CONVERSIONS_EVENTS, VIEW_CONTENT_TYPES } from '@definitions/conversions.types';
import api from '@services/nextAPI';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { PastArtworks } from '@components/blocks/pastArtworks';

import {
  Container,
  Logo,
  PlatformArtistLink,
  GalleryGrid,
  GalleryColumn,
  OnPlatformContainer,
  Title,
  AddressLink,
  AddressItem,
  AddressTitle,
  AddressLinkLine,
  AddressLinkLineCountry,
  ImpressionsImageContainer,
  ArtworksContainer,
  HorizontalLine,
  OtherTitles,
} from './galleryView.styles';

export interface GalleryViewProps {
  gallery: GalleryPage;
}

export const CONTENT_ARTWORKS_ELEMENT_NAME = 'Artworks';
export const CONTENT_ABOUT_ELEMENT_NAME = 'About';
export const CONTENT_ARTIST_ELEMENT_NAME = 'Artists';

export const GalleryView = ({ gallery }: GalleryViewProps) => {
  const { isMobile } = useBreakpoint();
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const { customer } = useCustomer();
  const getGoogleSearchQuery = (address: string) => `https://www.google.com/maps/search/?api=1&query=${address}`;
  const formatGalleryAddress = (address: Address) =>
    `${gallery.name}: ${address.addressStreet}, ${address.addressCity}, ${address.addressState} - ${address.addressCountry}`;
  const intl = useIntl();
  const { addMonogram, clearMonogram } = useMonogram();
  const { sendConversion } = useTrackConversions();
  const hasProducts = getHasProducts(gallery.galleryProducts);
  const hasPastProducts = getHasProducts(gallery.pastGalleryProducts);
  const newsletterSuccessInformation = intl.formatMessage({
    id: 'galleryView.newsletterSuccessInformation',
    defaultMessage: "Thank you! You've successfully subscribed to the gallery newsletter.",
  });
  const newsletterTitle = intl.formatMessage({
    id: 'galleryView.newsletterTitle',
    defaultMessage: 'Newsletter:',
  });
  const newsletterInfoText = intl.formatMessage(
    {
      id: 'galleryView.newsletterInfoText',
      defaultMessage: 'Sign up for updates about {galleryName} and other galleries.',
    },
    { galleryName: gallery.name }
  );

  useAsync(async () => {
    if (gallery.galleryProducts.length > 0 && gallery.pastGalleryProducts.length > 0) {
      gallery.galleryProducts[0].value = [...gallery.galleryProducts[0].value, ...gallery.pastGalleryProducts[0].value];
    }

    const id = new Date().getTime();

    if (gallery.monogram?.monogramLetter?.length) {
      const { monogramLetter, monogramColor, monogramAnimationType } = gallery.monogram;

      addMonogram({
        letter: monogramLetter,
        color: monogramColor,
        appearance: monogramAnimationType,
        id,
      });
    }

    await sendConversion({
      eventType: CONVERSIONS_EVENTS.VIEW_CONTENT,
      contentName: VIEW_CONTENT_TYPES.GALLERY_PLP,
    });

    return () => {
      clearMonogram(id);
    };
  }, []);

  const handleNotifySubmit = async (e: FormEvent, formData: { email: string; firstName: string; lastName: string }) => {
    e.preventDefault();
    const { email, firstName, lastName } = formData;
    await api.post('email/subscribe', {
      email,
      firstName,
      lastName,
      tags: [`Gallery page ID ${gallery.id}`],
    });
    setIsConfirmationVisible(true);
  };

  const renderAddress = (address: Address, index: number) => {
    const renderValidAddress = renderWhenTrue(
      always(
        <AddressItem key={index}>
          <AddressTitle>{capitalizeWords(gallery.name)}</AddressTitle>
          <AddressLink
            target="_blank"
            rel="noopener noreferrer"
            href={getGoogleSearchQuery(formatGalleryAddress(address))}
          >
            <AddressLinkLine>{address.addressStreet}</AddressLinkLine>
            <AddressLinkLine>{address.addressCity}</AddressLinkLine>
            <AddressLinkLineCountry>{address.addressCountry}</AddressLinkLineCountry>
          </AddressLink>
        </AddressItem>
      )
    );

    return renderValidAddress(!isNil(address.addressCity));
  };

  const renderImpression = (impression: ImageSet, index: number) => (
    <ImpressionsImageContainer key={index}>
      <ImageBlock image={impression} mobileImage={impression} isPlainImage />
    </ImpressionsImageContainer>
  );

  const renderImpressions = renderWhenTrue(
    always(
      <GalleryGrid>
        <GalleryColumn>
          <Title>
            <FormattedMessage id="galleryView.About" defaultMessage="About:" />
          </Title>
        </GalleryColumn>
        <GalleryColumn>{gallery.impressions.map(renderImpression)}</GalleryColumn>
      </GalleryGrid>
    )
  );

  const renderPlatformArtist = ({ slug, title }: OnPlatformArtist) => (
    <PlatformArtistLink
      href={`/artists/${slug}`}
      onClick={() => {
        window.history.pushState({}, '', `/artists/${slug}`);
      }}
    >
      {title}
    </PlatformArtistLink>
  );

  const renderPlatformArtistPastContainer = renderWhen(complement(isEmpty), (pastArtists: OnPlatformArtist[]) => (
    <OnPlatformContainer>{pastArtists.map(renderPlatformArtist)}</OnPlatformContainer>
  ));

  const renderOnPlatform = ({ current, past }: OnPlatform) => (
    <OnPlatformContainer>
      {current.map(renderPlatformArtist)}
      {renderPlatformArtistPastContainer(past)}
    </OnPlatformContainer>
  );

  const renderProductsBlock = ({ value }: Block, index: number) => <ProductsBlock key={index} artworks={value} />;

  const renderGalleryProducts = renderWhenTrue(always(gallery.galleryProducts.map(renderProductsBlock)));

  const renderPastArtworks = renderWhenTrue(
    always(<PastArtworks blocks={gallery.pastGalleryProducts} marginTop={20} />)
  );

  const renderNoArtworks = renderWhenTrue(always(<NoArtworks />));

  const renderButtons = always(
    <>
      <Title>
        <FormattedMessage id="galleryView.headerTitle" defaultMessage="Gallery:" />
      </Title>
      <div style={{ marginLeft: 30, marginTop: 10 }}>
        <JumpToSectionButton elementName={CONTENT_ARTWORKS_ELEMENT_NAME} />
        <JumpToSectionButton elementName={CONTENT_ABOUT_ELEMENT_NAME} />
        <JumpToSectionButton elementName={CONTENT_ARTIST_ELEMENT_NAME} />
      </div>
    </>
  );

  const renderReducedView = always(
    <Container>
      <GalleryGrid>
        <GalleryColumn>
          <Title>
            <FormattedMessage id="galleryView.headerTitle" defaultMessage="Gallery:" />
          </Title>
        </GalleryColumn>
        <GalleryColumn>
          {gallery.gallery.addresses.map(renderAddress)}
          <NotifyForm
            onFormSubmit={(e, { email, firstName, lastName }) => handleNotifySubmit(e, { email, firstName, lastName })}
            isConfirmationVisible={isConfirmationVisible}
            texts={{
              info: '',
              confirmation: 'You will be notified when the gallery becomes available.',
              submit: 'SIGN UP',
            }}
            initialFields={{
              email: customer?.email,
              firstName: customer?.firstName,
              lastName: customer?.lastName,
            }}
          />
        </GalleryColumn>
      </GalleryGrid>
    </Container>
  );

  const renderFullView = always(
    <Container>
      <GalleryGrid>
        <GalleryColumn>
          {!isMobile ? (
            renderButtons()
          ) : (
            <Title>
              <FormattedMessage id="galleryView.headerTitle" defaultMessage="Gallery:" />
            </Title>
          )}
        </GalleryColumn>
        <GalleryColumn>
          <Logo>
            <ImageBlock image={gallery.logo} mobileImage={gallery.logo} isPlainImage />
          </Logo>
          {gallery.gallery.addresses.map(renderAddress)}
        </GalleryColumn>
      </GalleryGrid>

      <Element name={CONTENT_ARTWORKS_ELEMENT_NAME}>
        <HorizontalLine />

        <OtherTitles>
          <FormattedMessage id="galleryView.headerTitle" defaultMessage="Artworks:" />
        </OtherTitles>
        <ArtworksContainer>
          {renderGalleryProducts(hasProducts)}
          {renderPastArtworks(hasPastProducts && !hasProducts)}
          {renderNoArtworks(!hasProducts && !hasPastProducts)}
        </ArtworksContainer>
      </Element>

      <Element name={CONTENT_ABOUT_ELEMENT_NAME}>
        {(!!gallery.impressions.length || !!gallery.galleryBio) && <HorizontalLine />}
        {renderImpressions(!!gallery.impressions.length)}
        {!!gallery.galleryBio && (
          <GalleryGrid>
            {gallery.impressions.length ? (
              <GalleryColumn />
            ) : (
              <GalleryColumn>
                <Title>
                  <FormattedMessage id="galleryView.About" defaultMessage="About:" />
                </Title>
              </GalleryColumn>
            )}
            <GalleryColumn>{gallery.galleryBio}</GalleryColumn>
          </GalleryGrid>
        )}
      </Element>

      <HorizontalLine />

      <Element name={CONTENT_ARTIST_ELEMENT_NAME}>
        <GalleryGrid>
          <GalleryColumn>
            <Title>
              <FormattedMessage id="galleryView.onPlatform" defaultMessage="Artists:" />
            </Title>
          </GalleryColumn>
          <GalleryColumn>{renderOnPlatform(gallery.onPlatform)}</GalleryColumn>
        </GalleryGrid>
      </Element>

      <HorizontalLine />
      {/* <ContainerInner> */}
      <NewsletterBlock
        infoText={newsletterInfoText}
        successInformation={newsletterSuccessInformation}
        tag={gallery.name}
        title={newsletterTitle}
      />
      {/* </ContainerInner> */}
    </Container>
  );

  const renderView = renderWhenTrueOtherwise(renderReducedView, renderFullView);

  return renderView(gallery.reducedVersion);
};
